import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { History, Plus, Truck, X } from "lucide-react";
import { API } from "../../../lib/apiClient";
import { Field, Modal } from "../Shared";

export function ChallanModal({ inventory, contacts, onClose, onSaved }) {
  const [form, setForm] = useState({ client_name: "", destination: "", vehicle_no: "", driver_name: "", notes: "" });
  const [items, setItems] = useState([{ item_id: inventory[0]?.id || "", quantity: 1 }]);
  const [saveContact, setSaveContact] = useState(false);
  const [selectedContact, setSelectedContact] = useState("");
  const [ledger, setLedger] = useState(null);
  const [ledgerLoading, setLedgerLoading] = useState(false);
  const update = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const applyContact = async (id) => {
    setSelectedContact(id);
    if (!id) { setLedger(null); return; }
    const c = contacts.find((x) => x.id === id);
    if (!c) return;
    setForm((f) => ({ ...f, client_name: c.client_name, destination: c.destination }));
    setLedgerLoading(true);
    try {
      const res = await axios.get(`${API}/challans/client-history`, { params: { client_name: c.client_name } });
      setLedger(res.data);
    } catch (err) { setLedger([]); } finally { setLedgerLoading(false); }
  };

  const submit = async (e) => {
    e.preventDefault();
    try {
      const filteredItems = items.filter((i) => i.item_id).map((i) => ({ ...i, quantity: Number(i.quantity) }));
      if (filteredItems.length === 0) { toast.error("Add at least one product"); return; }
      await axios.post(`${API}/challans`, { ...form, items: filteredItems });
      if (saveContact && form.client_name && form.destination) {
        try { await axios.post(`${API}/contacts`, { client_name: form.client_name, destination: form.destination }); } catch (_) {}
      }
      toast.success("Dispatch recorded and stock reduced");
      onSaved(); onClose();
    } catch (err) { toast.error(err.response?.data?.detail || "Could not create dispatch"); }
  };

  return <Modal title="New delivery challan" onClose={onClose}>
    <form onSubmit={submit} className="modal-form">
      {contacts && contacts.length > 0 && <label>Saved contacts (optional)
        <select data-testid="challan-contact-select" value={selectedContact} onChange={(e) => applyContact(e.target.value)}>
          <option value="">— Pick a saved client to auto-fill —</option>
          {contacts.map((c) => <option key={c.id} value={c.id}>{c.client_name} · {c.destination}</option>)}
        </select>
      </label>}
      {selectedContact && <div className="client-ledger" data-testid="client-ledger">
        <div className="ledger-heading"><History size={14} /><b>Past dispatches · {form.client_name}</b><span data-testid="client-ledger-count">{ledgerLoading ? "…" : `${(ledger || []).length} total`}</span></div>
        {ledgerLoading ? <p className="ledger-empty">Loading history…</p> :
          (ledger || []).length === 0 ? <p className="ledger-empty" data-testid="client-ledger-empty">No previous dispatches for this client yet.</p> :
          <div className="ledger-list" data-testid="client-ledger-list">
            {ledger.map((c) => <div className="ledger-row" key={c.id} data-testid={`ledger-row-${c.id}`}>
              <b>{c.challan_no}</b>
              <span>{new Date(c.date).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" })}</span>
              <span className="ledger-dest">{c.destination}</span>
              <span>{c.items.length} item{c.items.length !== 1 ? "s" : ""}</span>
            </div>)}
          </div>}
      </div>}
      <div className="form-grid">
        <Field label="Client name" name="client_name" value={form.client_name} onChange={update} required testid="challan-client-input" />
        <Field label="Destination" name="destination" value={form.destination} onChange={update} required testid="challan-destination-input" />
        <Field label="Vehicle number" name="vehicle_no" value={form.vehicle_no} onChange={update} required testid="challan-vehicle-input" />
        <Field label="Driver name" name="driver_name" value={form.driver_name} onChange={update} required testid="challan-driver-input" />
      </div>
      <label style={{ flexDirection: "row", alignItems: "center", gap: 8, fontSize: 12, color: "#405340" }}>
        <input type="checkbox" data-testid="challan-save-contact-checkbox" checked={saveContact} onChange={(e) => setSaveContact(e.target.checked)} style={{ width: 15, height: 15, padding: 0 }} />
        Save this client & destination for reuse
      </label>
      <label>Notes (optional)
        <textarea data-testid="challan-notes-input" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Purchase order, invoice reference, handling notes…" />
      </label>
      <div className="line-items">
        <div className="line-heading"><b>Products to send</b><button type="button" data-testid="challan-add-line-button" onClick={() => setItems([...items, { item_id: inventory[0]?.id || "", quantity: 1 }])}><Plus size={14} /> Add line</button></div>
        {items.map((item, index) => <div className="line-row" key={index}>
          <select data-testid={`challan-item-select-${index}`} value={item.item_id} onChange={(e) => setItems(items.map((x, i) => i === index ? { ...x, item_id: e.target.value } : x))}>{inventory.map((inv) => <option key={inv.id} value={inv.id}>{inv.name} · {inv.quantity} {inv.unit}</option>)}</select>
          <input data-testid={`challan-item-quantity-${index}`} type="number" min="0.01" step="0.01" value={item.quantity} onChange={(e) => setItems(items.map((x, i) => i === index ? { ...x, quantity: e.target.value } : x))} />
          <button type="button" data-testid={`challan-remove-line-${index}`} onClick={() => setItems(items.filter((_, i) => i !== index))}><X size={16} /></button>
        </div>)}
      </div>
      <div className="modal-actions">
        <button type="button" className="button ghost" data-testid="challan-cancel-button" onClick={onClose}>Cancel</button>
        <button type="submit" className="button primary" data-testid="challan-save-button"><Truck size={16} /> Save dispatch</button>
      </div>
    </form>
  </Modal>;
}
